import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { PauseCircle, PlayCircle, ExternalLink, X, Save, CheckCircle2, XCircle } from 'lucide-react';
import { format } from 'date-fns';
import { ko } from 'date-fns/locale';
import { toast } from 'sonner';
import api from '@/lib/api';
import AdminLayout from '@/components/AdminLayout';
import AdminConfirm from '@/components/AdminConfirm';
import { FilterBar, SelectPill, Pagination, StateMsg } from '@/components/AdminFilters';

const STATUS_OPTS = [
  { value: 'all',       label: '모든 상태' },
  { value: 'pending',   label: '심사 중' },
  { value: 'approved',  label: '승인' },
  { value: 'rejected',  label: '반려' },
  { value: 'suspended', label: '정지' },
];
const CATEGORY_OPTS = [
  { value: 'all', label: '모든 카테고리' },
  { value: 'fashion', label: '패션' }, { value: 'beauty', label: '뷰티' },
  { value: 'food', label: '식품' }, { value: 'pet', label: '펫' },
  { value: 'kids', label: '키즈' }, { value: 'living', label: '리빙' },
  { value: 'electronics', label: '전자제품' }, { value: 'others', label: '기타' },
];

const statusBadge = (s) => {
  const map = {
    approved:  { text: '승인',    cls: 'bg-emerald-500/10 text-emerald-600' },
    pending:   { text: '심사 중', cls: 'bg-amber-500/10 text-amber-600' },
    rejected:  { text: '반려',    cls: 'bg-red-500/10 text-red-600' },
    suspended: { text: '정지',    cls: 'bg-gray-500/10 text-gray-500' },
  };
  return map[s] || { text: s || '-', cls: 'bg-black/5' };
};

const AdminSellers = ({ user }) => {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') || '');
  const [status, setStatus] = useState(params.get('status') || 'all');
  const [category, setCategory] = useState(params.get('category') || 'all');
  const [page, setPage] = useState(Number(params.get('page') || 1));
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [tab, setTab] = useState('basic');
  const [memoDraft, setMemoDraft] = useState('');
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/sellers', {
        params: { q: q || undefined, status: status !== 'all' ? status : undefined, category: category !== 'all' ? category : undefined, page, page_size: 20 },
      });
      setData(res.data);
    } catch (e) { toast.error(e?.response?.data?.detail || '목록을 불러오지 못했습니다.'); }
    finally { setLoading(false); }
  }, [q, status, category, page]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const p = {};
    if (q) p.q = q; if (status !== 'all') p.status = status; if (category !== 'all') p.category = category;
    if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [q, status, category, page, setParams]);

  const reset = () => { setQ(''); setStatus('all'); setCategory('all'); setPage(1); };

  const openDetail = async (s) => {
    try {
      const res = await api.get(`/admin/sellers/${s.seller_id}`);
      setDetail(res.data); setMemoDraft(res.data.admin_memo || ''); setTab('basic');
    } catch (e) { toast.error('상세를 불러오지 못했습니다.'); }
  };

  const run = async (fn, msg) => {
    try { const r = await fn(); toast.success(msg || r.data?.message || '완료되었습니다.'); load(); if (detail) { openDetail(detail); } }
    catch (e) { toast.error(e?.response?.data?.detail || '실패했습니다.'); }
  };

  const approve = (s) => run(() => api.post(`/admin/sellers/${s.seller_id}/approve`), '판매자가 승인되었습니다.');
  const reject = (s) => run(() => api.post(`/admin/sellers/${s.seller_id}/reject`, { reason: '' }));
  const suspend = (s) => setConfirm({
    title: '판매자를 정지하시겠습니까?',
    message: `${s.name} 판매자는 소비자 화면과 편성표에서 즉시 숨겨집니다.`,
    confirmLabel: '정지',
    onConfirm: () => run(() => api.post(`/admin/sellers/${s.seller_id}/suspend`)),
  });
  const unsuspend = (s) => run(() => api.post(`/admin/sellers/${s.seller_id}/unsuspend`));

  const saveMemo = async () => {
    try { const r = await api.put(`/admin/sellers/${detail.seller_id}/memo`, { admin_memo: memoDraft }); toast.success(r.data.message); }
    catch (e) { toast.error('메모 저장 실패'); }
  };

  const items = data.items || [];

  return (
    <AdminLayout user={user} active="sellers">
      <div className="mb-4">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>판매자 관리</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">판매자 승인 · 반려 · 정지 · 메모 등을 관리합니다.</p>
      </div>

      <FilterBar q={q} setQ={setQ} onSearch={() => { setPage(1); load(); }} onReset={reset} placeholder="판매자명 또는 이메일 검색">
        <SelectPill value={status}   onChange={(v) => { setStatus(v); setPage(1); }}   options={STATUS_OPTS}   testid="filter-status" />
        <SelectPill value={category} onChange={(v) => { setCategory(v); setPage(1); }} options={CATEGORY_OPTS} testid="filter-category" />
      </FilterBar>

      <div className="bg-white dark:bg-[#141414] border border-black/5 dark:border-white/10 rounded-xl overflow-hidden" data-testid="admin-sellers-table">
        <StateMsg loading={loading} empty={!loading && items.length === 0} />
        {!loading && items.length > 0 && (
          <>
            <table className="hidden md:table w-full text-sm">
              <thead className="bg-black/5 dark:bg-white/5 text-xs uppercase tracking-wider text-gray-500 text-left">
                <tr>
                  <th className="px-4 py-2">판매자</th>
                  <th className="px-4 py-2">상태</th>
                  <th className="px-4 py-2">카테고리</th>
                  <th className="px-4 py-2">방송</th>
                  <th className="px-4 py-2">상품</th>
                  <th className="px-4 py-2">팔로워</th>
                  <th className="px-4 py-2 text-right">작업</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5 dark:divide-white/10">
                {items.map((s) => {
                  const sb = statusBadge(s.effective_status);
                  return (
                    <tr key={s.seller_id} className="hover:bg-black/5 dark:hover:bg-white/5 cursor-pointer" onClick={() => openDetail(s)} data-testid={`seller-row-${s.seller_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-bold">{s.name}</div>
                        <div className="text-xs text-gray-500">{s.user_email}</div>
                      </td>
                      <td className="px-4 py-3"><span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${sb.cls}`}>{sb.text}</span></td>
                      <td className="px-4 py-3 text-xs text-gray-500">{(s.categories || []).join(', ')}</td>
                      <td className="px-4 py-3 text-xs">{s.broadcast_count}</td>
                      <td className="px-4 py-3 text-xs">{s.product_count}</td>
                      <td className="px-4 py-3 text-xs">{s.follower_count || 0}</td>
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <div className="flex justify-end gap-1">
                          {s.effective_status !== 'approved' && <button onClick={() => approve(s)} className="text-emerald-600 hover:underline text-xs font-bold" data-testid={`seller-approve-${s.seller_id}`}>승인</button>}
                          {s.effective_status !== 'suspended' ? (
                            <button onClick={() => suspend(s)} className="text-amber-600 hover:underline text-xs font-bold ml-2" data-testid={`seller-suspend-${s.seller_id}`}>정지</button>
                          ) : (
                            <button onClick={() => unsuspend(s)} className="text-emerald-600 hover:underline text-xs font-bold ml-2" data-testid={`seller-unsuspend-${s.seller_id}`}>정지 해제</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <div className="md:hidden divide-y divide-black/5 dark:divide-white/10">
              {items.map((s) => {
                const sb = statusBadge(s.effective_status);
                return (
                  <button key={s.seller_id} type="button" onClick={() => openDetail(s)}
                    className="w-full text-left px-4 py-3 flex flex-col gap-1 hover:bg-black/5 dark:hover:bg-white/5"
                    data-testid={`seller-card-${s.seller_id}`}>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-sm truncate">{s.name}</span>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${sb.cls}`}>{sb.text}</span>
                    </div>
                    <div className="text-xs text-gray-500 truncate">{s.user_email}</div>
                    <div className="text-[11px] text-gray-400">방송 {s.broadcast_count} · 상품 {s.product_count} · 팔로워 {s.follower_count || 0}</div>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
      <Pagination page={page} total={data.total} pageSize={20} onChange={setPage} />

      {detail && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-3" onClick={() => setDetail(null)}>
          <div className="w-full max-w-2xl bg-white dark:bg-[#141414] rounded-2xl border border-black/10 dark:border-white/10 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="seller-detail-modal">
            <div className="flex items-center justify-between px-5 py-4 border-b border-black/5 dark:border-white/10">
              <div>
                <h3 className="font-black text-lg">{detail.name}</h3>
                <p className="text-xs text-gray-500">{detail.user?.email}</p>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/seller/${detail.seller_id}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#FF3B30] hover:underline" data-testid="seller-public-link">
                  <ExternalLink size={12} /> 공개 프로필
                </Link>
                <button onClick={() => setDetail(null)} className="p-1"><X size={16} /></button>
              </div>
            </div>
            <div className="flex border-b border-black/5 dark:border-white/10 px-3">
              {[
                ['basic', '기본 정보'], ['broadcasts', `방송 (${(detail.broadcasts || []).length})`],
                ['products', `상품 (${(detail.products || []).length})`], ['history', '처리 이력'], ['memo', '관리자 메모'],
              ].map(([k, l]) => (
                <button key={k} onClick={() => setTab(k)}
                  className={`px-3 py-2 text-xs font-bold border-b-2 ${tab === k ? 'border-[#FF3B30] text-[#FF3B30]' : 'border-transparent text-gray-500'}`}
                  data-testid={`seller-tab-${k}`}>{l}</button>
              ))}
            </div>

            <div className="p-5 space-y-3 text-sm">
              {tab === 'basic' && (
                <>
                  <div><span className="text-gray-500">카테고리</span> · {(detail.categories || []).join(', ') || '-'}</div>
                  <div><span className="text-gray-500">팔로워</span> · {detail.follower_count || 0}</div>
                  <div><span className="text-gray-500">가입일</span> · {detail.created_at?.slice(0, 10)}</div>
                  <div className="pt-2 flex flex-wrap gap-2">
                    {detail.effective_status !== 'approved' && <button onClick={() => approve(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-600 text-white"><CheckCircle2 size={12} /> 승인</button>}
                    <button onClick={() => reject(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-red-600 text-white"><XCircle size={12} /> 반려</button>
                    {detail.effective_status !== 'suspended' ? (
                      <button onClick={() => suspend(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-500 text-white"><PauseCircle size={12} /> 정지</button>
                    ) : (
                      <button onClick={() => unsuspend(detail)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-600 text-white"><PlayCircle size={12} /> 정지 해제</button>
                    )}
                  </div>
                </>
              )}
              {tab === 'broadcasts' && (
                <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
                  {(detail.broadcasts || []).map((b) => (
                    <li key={b.schedule_id} className="flex justify-between py-1 border-b border-dashed border-black/5">
                      <span className="truncate mr-2">{b.title}</span>
                      <span className="text-gray-500 shrink-0">{b.scheduled_at?.slice(0, 16)}</span>
                    </li>
                  ))}
                  {(detail.broadcasts || []).length === 0 && <li className="text-gray-500">등록된 방송이 없습니다.</li>}
                </ul>
              )}
              {tab === 'products' && (
                <ul className="text-xs space-y-1 max-h-64 overflow-y-auto">
                  {(detail.products || []).map((p) => (
                    <li key={p.product_id} className="flex justify-between py-1 border-b border-dashed border-black/5">
                      <span className="truncate mr-2">{p.name}</span>
                      <span className="text-gray-500 shrink-0">{p.sale_status}</span>
                    </li>
                  ))}
                  {(detail.products || []).length === 0 && <li className="text-gray-500">등록된 상품이 없습니다.</li>}
                </ul>
              )}
              {tab === 'history' && (
                <ul className="text-xs space-y-1 max-h-64 overflow-y-auto" data-testid="seller-history-list">
                  {(detail.history || []).map((h) => (
                    <li key={h.log_id} className="py-1 border-b border-dashed border-black/5">
                      <span className="font-bold">{h.action}</span>
                      <span className="text-gray-500 ml-2">by {h.admin_email}</span>
                      <span className="text-gray-400 ml-2">{h.created_at?.slice(0, 16)}</span>
                    </li>
                  ))}
                  {(detail.history || []).length === 0 && <li className="text-gray-500">이력이 없습니다.</li>}
                </ul>
              )}
              {tab === 'memo' && (
                <>
                  <textarea value={memoDraft} onChange={(e) => setMemoDraft(e.target.value)} rows={5}
                    className="w-full px-3 py-2 border rounded-md bg-white dark:bg-[#141414] border-black/10 dark:border-white/10 text-sm" data-testid="seller-memo-input" />
                  <button onClick={saveMemo} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold bg-[#FF3B30] text-white" data-testid="seller-memo-save">
                    <Save size={12} /> 저장
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
      <AdminConfirm state={confirm} onClose={() => setConfirm(null)} />
    </AdminLayout>
  );
};

export default AdminSellers;
